import { computed, reactive, ref } from 'vue'
import { isAxiosError } from 'axios'
import { handleAsync, isLoading } from 'best-modules/plugins'
import useVuelidate from '@vuelidate/core'
import { email, helpers, required } from '@vuelidate/validators'
import { defineLocalStore } from '@/utils/localStore'
import { getErrorMessages } from '@/utils/helpers'
import {
  currentSession,
  checkEmail,
  passwordLogin,
  setupAuthenticator,
  confirmAuthenticator,
  verifyAuthenticator,
} from '@/requests/sso'
import type {
  AuthSessionResponse,
  AuthCredentials,
  AuthErrorResponse,
  AuthPortalUser,
  AuthRememberedUser,
  AuthSetupResponse,
  AuthStep,
  AuthSuccessResponse,
} from '@/types/sso'

const REMEMBERED_EMAIL_STORAGE_KEY = 'ssoRememberedEmail'
const REMEMBERED_USER_STORAGE_KEY = 'ssoRememberedUser'

const getRememberedUser = (): AuthRememberedUser | null => {
  try {
    const saved = localStorage.getItem(REMEMBERED_USER_STORAGE_KEY)
    if (saved) {
      const user = JSON.parse(saved)
      if (
        typeof user?.email !== 'string' ||
        !user.email.trim() ||
        typeof user.name !== 'string' ||
        typeof user.initials !== 'string'
      )
        return null
      return { email: user.email.trim(), name: user.name, initials: user.initials }
    }
    const email = localStorage.getItem(REMEMBERED_EMAIL_STORAGE_KEY)?.trim()
    return email
      ? { email, name: '', initials: email.slice(0, 2).toUpperCase() }
      : null
  } catch {
    return null
  }
}

const ssoStore = defineLocalStore('sso', () => {
  const rememberedUser = ref<AuthRememberedUser | null>(getRememberedUser())
  const step = ref<AuthStep>(rememberedUser.value ? 'account' : 'email')
  const isSessionChecked = ref(false)
  const formError = ref('')
  const codeServerError = ref('')
  const challengeId = ref('')
  const fromSetup = ref(false)
  const setupData = ref<AuthSetupResponse | null>(null)
  const showManualKey = ref(false)
  const copied = ref(false)
  const rememberMe = ref(!!rememberedUser.value)
  const credentials = reactive<AuthCredentials>({
    email: rememberedUser.value?.email || '',
    password: '',
    code: '',
  })
  const portalUser = reactive<AuthPortalUser>({
    name: '',
    firstName: '',
    email: '',
    initials: '',
  })
  const links = {
    forgotPassword: import.meta.env.VITE_FORGOT_PASSWORD_URL,
    authenticatorRecovery: import.meta.env.VITE_AUTHENTICATOR_RECOVERY_URL,
    privacy: import.meta.env.VITE_PRIVACY_URL,
    support: import.meta.env.VITE_SUPPORT_URL,
    best24: import.meta.env.VITE_SSO_BEST24_URL,
    intime: import.meta.env.VITE_SSO_INTIME_URL,
    doctime: import.meta.env.VITE_SSO_DOCTIME_URL,
    finreport: import.meta.env.VITE_SSO_FINREPORT_URL,
  }
  const validation = useVuelidate(
    {
      email: {
        required: helpers.withMessage('Введіть корпоративну пошту', required),
        email: helpers.withMessage(
          'Перевірте формат пошти, наприклад name@company.ua',
          email
        ),
      },
      password: { required: helpers.withMessage('Введіть пароль', required) },
      code: {
        sixDigits: helpers.withMessage(
          'Введіть 6 цифр із застосунку Microsoft Authenticator',
          (value: string) => /^\d{6}$/.test(value)
        ),
      },
    },
    credentials
  )

  const codeErrorMessages = computed(() =>
    codeServerError.value
      ? [codeServerError.value]
      : getErrorMessages(validation.value.code)
  )
  const mfaHint = computed(() =>
    fromSetup.value
      ? 'Введіть 6-значний код, що з’явився в Microsoft Authenticator, щоб завершити налаштування.'
      : 'Відкрийте застосунок Microsoft Authenticator і введіть 6-значний код для облікового запису BEST LEASING.'
  )

  const errorMessage = (error: unknown): string => {
    const data = isAxiosError<AuthErrorResponse>(error)
      ? error.response?.data
      : undefined
    return (
      data?.errors?.email?.[0] ||
      data?.errors?.password?.[0] ||
      data?.errors?.code?.[0] ||
      data?.message ||
      'Не вдалося виконати запит. Спробуйте пізніше.'
    )
  }

  const completeLogin = (
    response: AuthSuccessResponse,
    saveRememberedUser = true
  ): void => {
    const email = response.user?.email?.trim() || credentials.email.trim()
    const name = response.user?.name?.trim() || ''
    const firstName =
      response.user?.firstName?.trim() || name.split(' ')[0] || ''
    const initials =
      response.user?.initials?.trim() ||
      name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase() ||
      email.slice(0, 2).toUpperCase()

    Object.assign(portalUser, { name, firstName, email, initials })
    if (saveRememberedUser) {
      rememberedUser.value = rememberMe.value ? { name, email, initials } : null
      try {
        if (rememberedUser.value) {
          localStorage.setItem(
            REMEMBERED_USER_STORAGE_KEY,
            JSON.stringify(rememberedUser.value)
          )
        } else {
          localStorage.removeItem(REMEMBERED_USER_STORAGE_KEY)
        }
        localStorage.removeItem(REMEMBERED_EMAIL_STORAGE_KEY)
      } catch {
        // Storage may be unavailable; successful login must still complete.
      }
    }
    validation.value.$reset()
    credentials.password = ''
    credentials.code = ''
    setupData.value = null
    if (response.redirectUrl) {
      window.location.href = response.redirectUrl
      return
    }
    step.value = 'portal'
  }

  const loadCurrentSession = (): Promise<void> => {
    return currentSession()
      .then((response: AuthSessionResponse) => {
        if (response.authenticated) {
          completeLogin({ status: 'authenticated', user: response.user }, false)
        }
      })
      .catch(() => undefined)
      .finally(() => {
        isSessionChecked.value = true
      })
  }

  const logout = (): void => {
    window.location.href = import.meta.env.VITE_AUTH_LOGOUT_URL || '/logout'
  }

  const submitEmail = (): Promise<void> | void => {
    if (isLoading('checkEmail')) return
    return validation.value.email
      .$validate()
      .then(isValid => {
        if (!isValid || isLoading('checkEmail')) return
        formError.value = ''
        return checkEmail({ email: credentials.email.trim() }).then(
          response => {
            credentials.email = response.email
            step.value = 'password'
          }
        )
      })
      .catch((error: unknown) => {
        formError.value = errorMessage(error)
      })
  }

  const loadSetup = (): Promise<void> => {
    return setupAuthenticator({ challengeId: challengeId.value }).then(
      response => {
        challengeId.value = response.challengeId
        setupData.value = response
        showManualKey.value = false
        copied.value = false
        fromSetup.value = true
        step.value = 'setup'
      }
    )
  }

  const selectRememberedUser = (): Promise<void> | void => {
    if (!rememberedUser.value || isLoading('checkEmail')) return
    credentials.email = rememberedUser.value.email
    credentials.password = ''
    credentials.code = ''
    challengeId.value = ''
    fromSetup.value = false
    setupData.value = null
    formError.value = ''
    codeServerError.value = ''
    rememberMe.value = true
    validation.value.$reset()
    return checkEmail({ email: credentials.email })
      .then(response => {
        credentials.email = response.email
        if (response.status === 'mfa' && response.challengeId?.trim()) {
          challengeId.value = response.challengeId
          step.value = 'mfa'
        } else {
          step.value = 'password'
        }
      })
      .catch((error: unknown) => {
        formError.value = errorMessage(error)
      })
  }

  const submitPassword = (): Promise<void> | void => {
    if (isLoading('loginWithPassword')) return
    formError.value = ''
    return handleAsync('loginWithPassword', () => {
      return validation.value.password.$validate().then(isValid => {
        if (!isValid) return
        return passwordLogin({
          email: credentials.email,
          password: credentials.password,
          rememberMe: false,
        }).then(response => {
          validation.value.password.$reset()
          credentials.password = ''
          if (response.status === 'authenticated') {
            completeLogin(response)
            return
          }
          challengeId.value = response.challengeId
          fromSetup.value = response.status === 'setup'
          if (fromSetup.value) return loadSetup()
          step.value = 'mfa'
        })
      })
    }).catch((error: unknown) => {
      formError.value = errorMessage(error)
    })
  }

  const returnToEmail = (): void => {
    if (
      isLoading([
        'checkEmail',
        'loginWithPassword',
        'setupAuthenticator',
        'confirmAuthenticator',
        'verifyAuthenticator',
      ])
    )
      return
    step.value = 'email'
    credentials.password = ''
    credentials.code = ''
    challengeId.value = ''
    setupData.value = null
    fromSetup.value = false
    showManualKey.value = false
    copied.value = false
    formError.value = ''
    codeServerError.value = ''
    validation.value.$reset()
  }

  const continueToMfa = (): void => {
    if (!setupData.value) return
    credentials.code = ''
    codeServerError.value = ''
    validation.value.code.$reset()
    step.value = 'mfa'
  }

  const useAnotherAccount = (): void => {
    returnToEmail()
    if (step.value !== 'email') return
    credentials.email = ''
    rememberMe.value = false
  }

  const openSetup = (): Promise<void> | void => {
    if (
      isLoading([
        'setupAuthenticator',
        'confirmAuthenticator',
        'verifyAuthenticator',
      ])
    )
      return
    formError.value = ''
    return loadSetup().catch((error: unknown) => {
      formError.value = errorMessage(error)
    })
  }

  const onCodeInput = (value: string | number | null): void => {
    credentials.code = String(value ?? '')
      .replace(/\D/g, '')
      .slice(0, 6)
    codeServerError.value = ''
    validation.value.code.$reset()
  }

  const verifyCode = (): Promise<void> | void => {
    if (
      isLoading([
        'setupAuthenticator',
        'confirmAuthenticator',
        'verifyAuthenticator',
      ])
    )
      return
    return validation.value.code
      .$validate()
      .then(isValid => {
        if (
          !isValid ||
          isLoading([
            'setupAuthenticator',
            'confirmAuthenticator',
            'verifyAuthenticator',
          ])
        )
          return
        codeServerError.value = ''
        const payload = {
          challengeId: challengeId.value,
          code: credentials.code,
          rememberDevice: false,
        }
        return (
          fromSetup.value
            ? confirmAuthenticator(payload)
            : verifyAuthenticator(payload)
        ).then(completeLogin)
      })
      .catch((error: unknown) => {
        codeServerError.value = errorMessage(error)
      })
  }

  const copySecret = (): Promise<void> | void => {
    if (!setupData.value?.secret || isLoading('copyAuthenticatorSecret')) return
    const secret = setupData.value.secret
    return handleAsync('copyAuthenticatorSecret', () => {
      return navigator.clipboard.writeText(secret).then(() => {
        copied.value = true
      })
    }).catch(() => {
      formError.value = 'Не вдалося скопіювати ключ. Скопіюйте його вручну.'
    })
  }

  const restart = (): void => {
    returnToEmail()
    rememberedUser.value = getRememberedUser()
    credentials.email = rememberedUser.value?.email || ''
    step.value = rememberedUser.value ? 'account' : 'email'
    Object.assign(portalUser, {
      name: '',
      firstName: '',
      email: '',
      initials: '',
    })
    rememberMe.value = !!credentials.email
  }

  return {
    step,
    isSessionChecked,
    loadCurrentSession,
    logout,
    formError,
    credentials,
    portalUser,
    validation,
    rememberMe,
    rememberedUser,
    setupData,
    showManualKey,
    copied,
    codeErrorMessages,
    mfaHint,
    links,
    submitEmail,
    selectRememberedUser,
    useAnotherAccount,
    submitPassword,
    returnToEmail,
    continueToMfa,
    openSetup,
    onCodeInput,
    verifyCode,
    copySecret,
    restart,
  }
})

export const initSso = ssoStore.initLocalStore
export const useSso = ssoStore.useLocalStore
